import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import SimpleBookViewer from '../components/SimpleBookViewer';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  BookOpen, 
  Plus, 
  Crown, 
  Calendar,
  Download,
  Eye,
  Loader,
  CheckCircle,
  XCircle,
  Clock,
  Palette,
  Sparkles,
  Target,
  Settings,
  BarChart3,
  Trash2,
  Coins,
  History,
  X,
  Minus
} from 'lucide-react';

const DashboardPage = () => {
  const { user, API_BASE_URL, credits } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedBook, setSelectedBook] = useState(null);
  const [downloadingBook, setDownloadingBook] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(null);
  const [generatingCover, setGeneratingCover] = useState(null);
  const [showCoverSelector, setShowCoverSelector] = useState(null);
  const [availableCovers, setAvailableCovers] = useState([]);
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [assigningCover, setAssigningCover] = useState(false);
  const [showMetadataSelector, setShowMetadataSelector] = useState(null);
  const [availableMetadata, setAvailableMetadata] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [assigningMetadata, setAssigningMetadata] = useState(false);
  const [deletingCover, setDeletingCover] = useState(null);
  
  // Credit history state
  const [creditHistory, setCreditHistory] = useState([]);
  const [loadingCreditHistory, setLoadingCreditHistory] = useState(false);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchBooks();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle lifetime offer purchase flow
  useEffect(() => {
    const shouldPurchaseLifetime = searchParams.get('purchase') === 'lifetime';
    if (shouldPurchaseLifetime && user) {
      // Trigger lifetime purchase automatically for seamless flow
      handleLifetimePurchase();
    }
  }, [searchParams, user]);

  const handleLifetimePurchase = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/lifetime-offer/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Set cookie to prevent banner from showing again
        document.cookie = 'lifetime_offer_purchased=true; expires=' + 
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString() + '; path=/';
        
        // Show success message and redirect to checkout
        toast.success('Redirecting to secure checkout...');
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 1000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate purchase');
        // Remove the purchase parameter from URL
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase');
      navigate('/dashboard', { replace: true });
    }
  };

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadMenu && !event.target.closest('.download-menu-container')) {
        setShowDownloadMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/books`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Failed to fetch books');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      setLoadingCreditHistory(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/credits/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
      toast.error('Failed to fetch credit history');
    } finally {
      setLoadingCreditHistory(false);
    }
  };

  const handleUpgrade = async (planType) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/create-checkout`, {
        plan_type: planType
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create payment session');
    }
  };

  const generateCoverForBook = async (book) => {
    setGeneratingCover(book.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${book.id}/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: 'fal',
          variations: 3,
          style_preferences: null,
          mood: null,
          target_audience: null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate cover');
      }

      const data = await response.json();
      toast.success('Cover generated successfully!');
      
      // Refresh books to show the new cover
      fetchBooks();
      
    } catch (error) {
      console.error('Error generating cover:', error);
      toast.error('Failed to generate cover. Please try again.');
    } finally {
      setGeneratingCover(null);
    }
  };

  const downloadBook = async (book, format) => {
    setDownloadingBook(`${book.id}-${format}`);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${book.id}/download/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create blob and download
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.content_type });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`${format.toUpperCase()} downloaded successfully!`);
        setShowDownloadMenu(null);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download book');
    } finally {
      setDownloadingBook(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      generating: {
        icon: <Loader className="h-4 w-4 animate-spin" />,
        text: 'Generating',
        className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
      },
      completed: {
        icon: <CheckCircle className="h-4 w-4" />,
        text: 'Completed',
        className: 'bg-green-500/20 text-green-300 border-green-500/30'
      },
      failed: {
        icon: <XCircle className="h-4 w-4" />,
        text: 'Failed',
        className: 'bg-red-500/20 text-red-300 border-red-500/30'
      }
    };

    const badge = badges[status] || badges.generating;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${badge.className}`}>
        {badge.icon}
        <span>{badge.text}</span>
      </span>
    );
  };

  const loadAvailableCovers = async () => {
    setLoadingCovers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCovers(data.covers || []);
      } else {
        throw new Error('Failed to load covers');
      }
    } catch (error) {
      console.error('Error loading covers:', error);
      toast.error('Failed to load available covers');
    } finally {
      setLoadingCovers(false);
    }
  };

  const assignCoverToBook = async (bookId, cover) => {
    setAssigningCover(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/assign-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cover_id: cover.id,
          cover_data: {
            image_base64: cover.image_base64,
            prompt: cover.prompt,
            provider: cover.provider || 'fal',
            width: cover.width,
            height: cover.height
          }
        })
      });

      if (response.ok) {
        toast.success('Cover assigned successfully!');
        setShowCoverSelector(null);
        // Refresh books to show the new cover
        fetchBooks();
      } else {
        throw new Error('Failed to assign cover');
      }
    } catch (error) {
      console.error('Error assigning cover:', error);
      toast.error('Failed to assign cover to book');
    } finally {
      setAssigningCover(false);
    }
  };

  const openCoverSelector = (book) => {
    setShowCoverSelector(book);
    loadAvailableCovers();
  };

  const loadAvailableMetadata = async () => {
    setLoadingMetadata(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableMetadata(data.metadata || []);
      } else {
        throw new Error('Failed to load metadata');
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast.error('Failed to load available metadata');
    } finally {
      setLoadingMetadata(false);
    }
  };

  const assignMetadataToBook = async (bookId, metadata) => {
    setAssigningMetadata(true);
    try {
      const token = localStorage.getItem('token');
      
      // Process keywords to extract strings from objects if needed
      const processedKeywords = metadata.optimized_keywords?.map(keyword => 
        typeof keyword === 'string' ? keyword : keyword.keyword || keyword
      ) || [];
      
      // Process categories to extract strings from objects if needed
      const processedCategories = metadata.optimized_categories?.map(category => 
        typeof category === 'string' ? category : category.category || category
      ) || [];
      
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/assign-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          metadata_id: metadata.id,
          metadata_data: {
            title: metadata.optimized_title,
            description: metadata.optimized_description,
            keywords: processedKeywords,
            categories: processedCategories,
            subtitle: metadata.optimized_subtitle
          }
        })
      });

      if (response.ok) {
        toast.success('Metadata assigned successfully!');
        setShowMetadataSelector(null);
        // Refresh books to show the updated metadata
        fetchBooks();
      } else {
        throw new Error('Failed to assign metadata');
      }
    } catch (error) {
      console.error('Error assigning metadata:', error);
      toast.error('Failed to assign metadata to book');
    } finally {
      setAssigningMetadata(false);
    }
  };

  const openMetadataSelector = (book) => {
    setShowMetadataSelector(book);
    loadAvailableMetadata();
  };

  const deleteCover = async (cover) => {
    setDeletingCover(cover.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/${cover.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Cover deleted successfully!');
        setShowDeleteConfirm(null);
        // Refresh the covers list
        loadAvailableCovers();
      } else {
        throw new Error('Failed to delete cover');
      }
    } catch (error) {
      console.error('Error deleting cover:', error);
      toast.error('Failed to delete cover');
    } finally {
      setDeletingCover(null);
    }
  };

  const confirmDeleteCover = (cover) => {
    setShowDeleteConfirm(cover);
  };

  const getCoverBadge = (cover) => {
    const coverType = cover.cover_type || 'unknown';
    
    const badgeConfig = {
      kdp_cover: {
        text: 'KDP Cover',
        className: 'bg-purple-500/80 text-white'
      },
      ebook_cover: {
        text: 'eBook Cover', 
        className: 'bg-blue-500/80 text-white'
      },
      book_cover: {
        text: 'Book Cover',
        className: 'bg-green-500/80 text-white'
      },
      unknown: {
        text: cover.source === 'book' ? 'Book Cover' : 'Generated',
        className: cover.source === 'book' ? 'bg-green-500/80 text-white' : 'bg-gray-500/80 text-white'
      }
    };

    const badge = badgeConfig[coverType] || badgeConfig.unknown;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  const BookPreview = ({ book, onClose }) => {
    if (!book || !book.content) return null;

    const chapters = Object.keys(book.content).sort((a, b) => parseInt(a) - parseInt(b));

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">{book.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {chapters.map((chapterNum) => (
              <div key={chapterNum} className="mb-8">
                <h3 className="text-xl font-semibold text-purple-300 mb-4">
                  Chapter {chapterNum}
                </h3>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {book.content[chapterNum]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const CoverSelector = ({ book, onClose }) => {
    if (!book) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Select Cover for "{book.title}"</h2>
              <p className="text-gray-400 mt-1">Choose from your generated covers</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loadingCovers ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 text-purple-400 animate-spin" />
                <span className="ml-2 text-gray-400">Loading covers...</span>
              </div>
            ) : availableCovers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {availableCovers.map((cover, index) => (
                  <div key={`${cover.id}-${index}`} className="group relative">
                    <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden border border-gray-600 hover:border-purple-500 transition-colors flex items-center justify-center">
                      <img
                        src={`data:image/png;base64,${cover.image_base64}`}
                        alt={`Cover for ${cover.title}`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          console.error('Cover image failed to load');
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    {/* Cover Info */}
                    <div className="mt-3">
                      <h3 className="text-white font-semibold text-sm mb-1 line-clamp-1">
                        {cover.title}
                      </h3>
                      <p className="text-gray-400 text-xs mb-1">
                        {cover.genre} • {new Date(cover.created_at).toLocaleDateString()}
                      </p>
                      {cover.dimensions && (
                        <p className="text-blue-400 text-xs mb-2">
                          {cover.book_format === 'paperback' ? `KDP ${cover.dimensions}` : `${cover.dimensions} px`}
                        </p>
                      )}
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => assignCoverToBook(book.id, cover)}
                          disabled={assigningCover}
                          className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:from-pink-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {assigningCover ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            'Select This Cover'
                          )}
                        </button>
                        
                        <button
                          onClick={() => confirmDeleteCover(cover)}
                          className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                          title="Delete Cover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Enhanced Badge */}
                    <div className="absolute top-2 left-2 z-10">
                      {getCoverBadge(cover)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Palette className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">
                  No covers available
                </h3>
                <p className="text-gray-500 mb-6">
                  Generate some covers first using the Cover Generator
                </p>
                <Link
                  to="/kdp-covers"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-colors"
                >
                  Generate Covers
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const MetadataSelector = ({ book, onClose }) => {
    if (!book) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Select Metadata for "{book.title}"</h2>
              <p className="text-gray-400 mt-1">Choose from your optimized metadata</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loadingMetadata ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 text-blue-400 animate-spin" />
                <span className="ml-2 text-gray-400">Loading metadata...</span>
              </div>
            ) : availableMetadata.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableMetadata.map((metadata, index) => (
                  <div key={`${metadata.id}-${index}`} className="bg-gray-800/50 rounded-lg p-4 border border-gray-600 hover:border-blue-500 transition-colors">
                    {/* Metadata Preview */}
                    <div className="mb-4">
                      <h3 className="text-white font-semibold text-lg mb-2 line-clamp-2">
                        {metadata.optimized_title || metadata.title}
                      </h3>
                      <p className="text-gray-300 text-sm mb-2">
                        {metadata.genre} • {new Date(metadata.created_at).toLocaleDateString()}
                      </p>
                      
                      {/* Description Preview */}
                      <div className="bg-gray-900/50 rounded p-3 mb-3">
                        <h4 className="text-blue-400 text-xs font-medium mb-1">DESCRIPTION:</h4>
                        <p className="text-gray-300 text-xs line-clamp-3">
                          {metadata.optimized_description}
                        </p>
                      </div>
                      
                      {/* Keywords Preview */}
                      <div className="mb-3">
                        <h4 className="text-green-400 text-xs font-medium mb-1">KEYWORDS:</h4>
                        <div className="flex flex-wrap gap-1">
                          {metadata.optimized_keywords?.slice(0, 3).map((keywordObj, idx) => (
                            <span key={idx} className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs">
                              {typeof keywordObj === 'string' ? keywordObj : keywordObj.keyword || keywordObj}
                            </span>
                          ))}
                          {metadata.optimized_keywords?.length > 3 && (
                            <span className="text-gray-400 text-xs">+{metadata.optimized_keywords.length - 3} more</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Categories Preview */}
                      {metadata.optimized_categories?.length > 0 && (
                        <div className="mb-3">
                          <h4 className="text-purple-400 text-xs font-medium mb-1">CATEGORIES:</h4>
                          <div className="flex flex-wrap gap-1">
                            {metadata.optimized_categories.slice(0, 2).map((categoryObj, idx) => (
                              <span key={idx} className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">
                                {typeof categoryObj === 'string' ? categoryObj : categoryObj.category || categoryObj}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Select Button */}
                    <button
                      onClick={() => assignMetadataToBook(book.id, metadata)}
                      disabled={assigningMetadata}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {assigningMetadata ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        'Select This Metadata'
                      )}
                    </button>
                    
                    {/* Optimization Badge */}
                    <div className="absolute top-2 right-2">
                      <span className="bg-blue-500/80 text-white px-2 py-1 text-xs rounded-full font-medium">
                        Optimized
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">
                  No metadata available
                </h3>
                <p className="text-gray-500 mb-6">
                  Generate some optimized metadata first using the Metadata Optimizer
                </p>
                <Link
                  to="/metadata"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors"
                >
                  Optimize Metadata
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Dashboard - Your AI BookMoney Books & Content"
        description="Manage your AI-generated books, covers, and metadata. Download, organize, and optimize your content for Amazon KDP publishing success."
        keywords="book dashboard, AI book management, KDP content management, book organization, AI writing dashboard, publishing dashboard"
        canonical="/dashboard"
        ogTitle="AI BookMoney Dashboard - Manage Your Books"
        ogDescription="Your personal publishing dashboard. Manage AI-generated books, covers, and metadata for Amazon KDP success."
        ogImage="/dashboard-preview.png"
        ogUrl="/dashboard"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "AI BookMoney Dashboard",
          "description": "Personal dashboard for managing AI-generated books and publishing content",
          "applicationCategory": "WritingApplication",
          "operatingSystem": "Web"
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Welcome back, {user.full_name}!
          </h1>
          <p className="text-gray-300">
            Create amazing books with AI - unlimited possibilities with your credits
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Books Generated */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center">
              <BookOpen className="h-10 w-10 text-purple-400 mr-4" />
              <div>
                <p className="text-2xl font-bold text-white">{user.books_generated}</p>
                <p className="text-gray-300">Books Generated</p>
              </div>
            </div>
          </div>

          {/* Credit Balance */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Coins className="h-10 w-10 text-yellow-400 mr-4" />
                <div>
                  <p className="text-2xl font-bold text-white">{credits || 0}</p>
                  <p className="text-gray-300">Credits</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreditHistory(!showCreditHistory);
                  if (!showCreditHistory && creditHistory.length === 0) {
                    fetchCreditHistory();
                  }
                }}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                <History className="h-5 w-5" />
              </button>
            </div>
            
            {/* Credit History Dropdown */}
            {showCreditHistory && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center">
                  <History className="h-4 w-4 mr-2" />
                  Recent Transactions
                </h4>
                
                {loadingCreditHistory ? (
                  <div className="flex justify-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                  </div>
                ) : creditHistory.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {creditHistory.slice(0, 5).map((transaction) => (
                      <div key={transaction.id} className="bg-gray-800 rounded p-2">
                        <div className="flex justify-between items-start text-xs">
                          <div className="flex-1">
                            <span className={`font-medium ${
                              transaction.amount > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                            </span>
                            <p className="text-gray-400 text-xs mt-1">
                              {transaction.description || transaction.operation.replace('_', ' ')}
                            </p>
                            {transaction.admin_info && (
                              <p className="text-gray-500 text-xs">
                                by {transaction.admin_info.name}
                              </p>
                            )}
                          </div>
                          <span className="text-gray-500 text-xs">
                            {new Date(transaction.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                    {creditHistory.length > 5 && (
                      <div className="text-center">
                        <button
                          onClick={() => setShowAllTransactions(true)}
                          className="text-xs text-blue-400 hover:text-blue-300 hover:underline"
                        >
                          View all {creditHistory.length} transactions
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs">No transactions yet</p>
                )}
              </div>
            )}
          </div>

          {/* Account Status */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center">
              {user.is_premium ? (
                <Crown className="h-10 w-10 text-yellow-400 mr-4" />
              ) : (
                <Calendar className="h-10 w-10 text-gray-400 mr-4" />
              )}
              <div>
                <p className="text-2xl font-bold text-white">
                  {user.is_premium ? 'Premium' : 'Free'}
                </p>
                <p className="text-gray-300">Account Type</p>
              </div>
            </div>
          </div>

          {/* Quick Action */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-semibold">Book Creation Wizard</p>
                <p className="text-purple-100 text-sm">5-step guided workflow</p>
              </div>
              <Link
                to="/create"
                className="bg-white text-purple-600 p-3 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Plus className="h-6 w-6" />
              </Link>
            </div>
          </div>
        </div>

        {/* Upgrade Banner for Free Users */}
        {!user.is_premium && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Upgrade to KDP Publishing Pro
                </h3>
                <p className="text-purple-100">
                  Get 75 credits monthly + unlimited access to all KDP tools
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleUpgrade('monthly')}
                  className="bg-white text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                >
                  $19/month
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Books Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Your Books</h2>
            <Link
              to="/generate"
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Quick Generate</span>
            </Link>
          </div>

          {books.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                No books yet
              </h3>
              <p className="text-gray-500 mb-6">
                Create your first AI-generated book to get started
              </p>
              <Link
                to="/create"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
              >
                Start with Book Creation Wizard
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {books.map((book) => (
                <div
                  key={book.id}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 hover:border-purple-400/40 transition-all duration-200 hover:transform hover:scale-105"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white truncate mr-2">
                      {book.title}
                    </h3>
                    {getStatusBadge(book.status)}
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-gray-300 text-sm">
                      <span className="font-medium">Genre:</span> {book.genre}
                    </p>
                    <p className="text-gray-300 text-sm">
                      <span className="font-medium">Chapters:</span> {book.chapters}
                    </p>
                    <p className="text-gray-300 text-sm">
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(book.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {book.status === 'completed' && (
                    <div className="space-y-3">
                      {/* Main Actions */}
                      <div className="flex space-x-2">
                        <Link
                          to={`/books/${book.id}`}
                          className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Manage Book</span>
                        </Link>
                        <button
                          onClick={() => setSelectedBook(book)}
                          className="bg-gray-700 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors flex items-center justify-center"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Publishing Status */}
                      <div className="bg-gray-800/50 rounded-lg p-3 border border-purple-500/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-300">KDP Ready:</span>
                          <span className="text-sm font-bold text-white">
                            {(() => {
                              let percentage = 50; // Base for content
                              if (book.has_cover) percentage += 25;
                              if (book.has_metadata) percentage += 25;
                              return `${percentage}%`;
                            })()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: (() => {
                                let percentage = 50; // Base for content
                                if (book.has_cover) percentage += 25;
                                if (book.has_metadata) percentage += 25;
                                return `${percentage}%`;
                              })()
                            }}
                          />
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-400">
                          <span>✓ Content</span>
                          <span className={book.has_cover ? 'text-green-400' : 'text-gray-400'}>
                            {book.has_cover ? '✓' : '○'} Cover
                          </span>
                          <span className={book.has_metadata ? 'text-green-400' : 'text-gray-400'}>
                            {book.has_metadata ? '✓' : '○'} Metadata
                          </span>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="grid grid-cols-4 gap-2">
                        <button
                          onClick={() => openCoverSelector(book)}
                          className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-3 py-2 rounded-lg font-medium hover:from-pink-700 hover:to-purple-700 transition-colors flex items-center justify-center"
                          title="Select Cover"
                        >
                          <Palette className="h-4 w-4" />
                        </button>
                        
                        <Link
                          to="/kdp-covers"
                          className="bg-gray-700 text-white px-3 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors flex items-center justify-center"
                          title="KDP Cover Generator"
                        >
                          <Sparkles className="h-4 w-4" />
                        </Link>
                        
                        <button
                          onClick={() => openMetadataSelector(book)}
                          className="bg-blue-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                          title="Select Metadata"
                        >
                          <Target className="h-4 w-4" />
                        </button>
                        
                        <Link
                          to="/metadata-optimizer"
                          state={{ bookData: book }}
                          className="bg-green-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
                          title="Optimize Metadata"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Link>
                        
                        <div className="relative download-menu-container">
                          <button
                            onClick={() => setShowDownloadMenu(showDownloadMenu === book.id ? null : book.id)}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
                            title="Download Files"
                          >
                            {downloadingBook?.startsWith(book.id) ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                          
                          {/* Drop Up Menu */}
                          {showDownloadMenu === book.id && (
                            <div className="absolute bottom-full mb-1 right-0 flex bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-[9999]">
                              {['pdf', 'epub', 'docx'].map((format, index) => (
                                <button
                                  key={format}
                                  onClick={() => downloadBook(book, format)}
                                  disabled={downloadingBook === `${book.id}-${format}`}
                                  className={`px-3 py-2 text-white hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm ${
                                    index === 0 ? 'rounded-l-lg' : index === 2 ? 'rounded-r-lg' : ''
                                  } ${index !== 2 ? 'border-r border-gray-600' : ''}`}
                                >
                                  {downloadingBook === `${book.id}-${format}` ? (
                                    <div className="flex items-center space-x-1">
                                      <Loader className="h-3 w-3 animate-spin" />
                                      <span className="text-xs">{format.toUpperCase()}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs font-medium">{format.toUpperCase()}</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {book.status === 'generating' && (
                    <div className="bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-lg text-center">
                      <Clock className="h-4 w-4 inline mr-2" />
                      Book is being generated...
                    </div>
                  )}

                  {book.status === 'failed' && (
                    <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-center">
                      <XCircle className="h-4 w-4 inline mr-2" />
                      Generation failed
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Book Viewer Modal */}
      {selectedBook && (
        <SimpleBookViewer
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
        />
      )}

      {/* Cover Selector Modal */}
      {showCoverSelector && (
        <CoverSelector
          book={showCoverSelector}
          onClose={() => setShowCoverSelector(null)}
        />
      )}

      {/* Metadata Selector Modal */}
      {showMetadataSelector && (
        <MetadataSelector
          book={showMetadataSelector}
          onClose={() => setShowMetadataSelector(null)}
        />
      )}

      {/* Delete Cover Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-500/20 p-3 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete Cover</h3>
                  <p className="text-gray-400 text-sm">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-300">
                  Are you sure you want to delete the cover for "{showDeleteConfirm.title}"?
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-gray-700 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteCover(showDeleteConfirm)}
                  disabled={deletingCover === showDeleteConfirm.id}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {deletingCover === showDeleteConfirm.id ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Cover'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* All Transactions Modal */}
      {showAllTransactions && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden border border-purple-500/20 shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-white">All Transactions</h2>
                <p className="text-gray-400 text-sm mt-1">{creditHistory.length} total transactions</p>
              </div>
              <button
                onClick={() => setShowAllTransactions(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(80vh-100px)] p-6">
              <div className="space-y-3">
                {creditHistory.map((transaction) => {
                  // Determine if it's a credit addition or deduction based on amount
                  const isAddition = transaction.amount > 0;
                  
                  return (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-purple-500/30 transition-colors"
                  >
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`p-2 rounded-lg ${
                        isAddition 
                          ? 'bg-green-500/20' 
                          : 'bg-red-500/20'
                      }`}>
                        {isAddition ? (
                          <Plus className="h-4 w-4 text-green-400" />
                        ) : (
                          <Minus className="h-4 w-4 text-red-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {transaction.description}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          {new Date(transaction.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <span className={`text-sm font-semibold ${
                        isAddition
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}>
                        {isAddition ? '+' : '-'}
                        {Math.abs(transaction.amount)}
                      </span>
                      <p className="text-gray-500 text-xs mt-1">
                        Balance: {transaction.balance_after}
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
              
              {creditHistory.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No transactions yet</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-700 bg-gray-800/30">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  Need more credits?
                </div>
                <Link
                  to="/credits/purchase"
                  onClick={() => setShowAllTransactions(false)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors text-sm font-medium"
                >
                  Top Up Credits
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default DashboardPage;